import { useAuth } from '../../context/AuthContext'
import { hasPermission as checkPermission, can as checkCan, getVisibleModules, Permission } from '../../lib/permissions'
import { ROLES } from '@jira-clone/shared-types'

/**
 * Reusable permission helper hook.
 *
 * Usage:
 *   const { can, cannot, hasAny, hasAll } = usePermissions()
 *   if (can('LEADS', 'VIEW')) { ... }
 *   if (hasAny('LEADS', ['VIEW', 'CREATE'])) { ... }
 */
export function usePermissions() {
  const { user } = useAuth()

  const hasPermission = (module: string, action: string): boolean => {
    return checkPermission(user?.permissions, module, action, user?.role)
  }

  const can = (module: string, action: string): boolean => {
    return checkCan(user?.permissions, module, action, user?.role)
  }

  const cannot = (module: string, action: string): boolean => {
    return !hasPermission(module, action)
  }

  const hasAny = (module: string, actions: string[]): boolean => {
    return actions.some((action) => hasPermission(module, action))
  }

  const hasAll = (module: string, actions: string[]): boolean => {
    return actions.every((action) => hasPermission(module, action))
  }

  const visibleModules = getVisibleModules(user?.permissions, user?.role)

  return {
    hasPermission,
    can,
    cannot,
    hasAny,
    hasAll,
    visibleModules,
    permissions: (user?.permissions || []) as Permission[],
    role: user?.role || ROLES.MEMBER,
  }
}
